import React, { useEffect, useMemo, useState } from 'react';
import socket from '../lib/socket';
import {
  getCategories, getRound, startRound,
  selectCategory, pickSong, sendAnswer, confirmPick
} from '../lib/singingApi';
import './singing.css';

const LADDER = [300000,150000,100000,60000,30000,20000,12000,9000,6000,3000];

export default function SingingAdmin(){
  const [score, setScore] = useState(0);
  const [categories, setCategories] = useState([]);
  const [currentCat, setCurrentCat] = useState(null);   // { id, name, songs, pickedSongIds }
  const [currentPick, setCurrentPick] = useState(null); // { pickId, song }
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cats = await getCategories();
        setCategories(cats?.categories || []);
      } catch {}

      try {
        const r = await getRound();
        const round = r?.round;
        if (round) setScore(Number(round.SCORE || 0));
      } catch {}
    })();
  }, []);

  // socket 同步（只更新分數與當前狀態）
  useEffect(() => {
    const onRound = ({ type, round }) => {
      if (type === 'round_started') {
        setScore(Number(round?.SCORE || 0));
        setCurrentCat(null);
        setCurrentPick(null);
        setInput('');
      }
    };
    const onCat = ({ categoryId, categoryName, songs, pickedSongIds = [] }) => {
      setCurrentCat({ id: categoryId, name: categoryName, songs, pickedSongIds });
      setCurrentPick(null);
      setInput('');
    };
    const onPlay = ({ pickId, song }) => {
      setCurrentPick({ pickId, song });
      setInput('');
    };
    const onResult = ({ score }) => setScore(Number(score || 0));
    const onBackHome = () => {
      setCurrentPick(null);
      setCurrentCat(null);
      setInput('');
    };

    socket.on('singing_game:state', onRound);
    socket.on('singing_game:category', onCat);
    socket.on('singing_game:play', onPlay);
    socket.on('singing_game:result', onResult);
    socket.on('singing_game:back_home', onBackHome);
    return () => {
      socket.off('singing_game:state', onRound);
      socket.off('singing_game:category', onCat);
      socket.off('singing_game:play', onPlay);
      socket.off('singing_game:result', onResult);
      socket.off('singing_game:back_home', onBackHome);
    };
  }, []);

  const activeStep = useMemo(() => {
    const idx = LADDER.findIndex(v => v <= score);
    return idx === -1 ? LADDER.length : idx;
  }, [score]);

  const onStart = async () => {
    setBusy(true);
    try { await startRound('現場來賓'); }
    catch { alert('開局失敗'); }
    finally { setBusy(false); }
  };

  const chooseCat = async (ID) => {
    setBusy(true);
    try { await selectCategory(ID); }
    catch { alert('選主題失敗'); }
    finally { setBusy(false); }
  };

  const chooseSong = async (sid) => {
    setBusy(true);
    try { await pickSong(sid); }
    catch (e) {
      if (String(e).includes('409')) alert('本輪已選過這首');
      else alert('選歌失敗');
    } finally { setBusy(false); }
  };

  const onType = async (v) => {
    setInput(v);
    if (currentPick?.pickId) {
      try { await sendAnswer(currentPick.pickId, v); } catch {}
    }
  };

const onConfirm = async () => {
  if (!currentPick?.pickId) return;
  setBusy(true);
  try {
    // 先盡量同步輸入（不管成不成功，confirm 都會再帶 input）
    try { await sendAnswer(currentPick.pickId, input); } catch {}

    const r = await fetch('/api/singing_game/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pickId: currentPick.pickId, input })
    });
    if (!r.ok) {
      const t = await r.text().catch(()=> '');
      throw new Error(t || 'HTTP ' + r.status);
    }
  } catch (e) {
    alert('確認失敗');
  } finally {
    setBusy(false);
  }
};



  const onBackHome = async () => {
    setBusy(true);
    try {
      await fetch('/api/singing_game/back_home', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      // 本地也先回主頁
      setCurrentPick(null);
      setCurrentCat(null);
      setInput('');
    } catch {
      alert('回主頁失敗');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sg-wrap admin">
      {/* 左側階梯 + 控制鍵 */}
      <div className="ladder">
        <button className="btn-start" disabled={busy} onClick={onStart}>開始 / 重置一輪</button>
        <button className="btn-backhome" disabled={busy} onClick={onBackHome}>回主頁</button>
        <div className="ladder-title small">目前分數：{score}</div>
        {LADDER.map((amt, i) => {
          const hl = i === activeStep;
          return (
            <div key={amt} className={`ladder-step ${hl ? 'active': ''}`}>
              <span>NT${amt.toLocaleString()}</span>
            </div>
          );
        })}
      </div>

      {/* 右側操作區 */}
      <div className="stage">
        {/* 主題格（六格） */}
        {!currentCat && !currentPick && (
          <div className="topic-panel">
            <div className="panel-title">請選主題</div>
            <div className="topics">
              {categories.map(c => (
                <button
                  key={c.ID}
                  className={`topic-pill color-${(c.ID%6)+1}`}
                  onClick={() => chooseCat(c.ID)}
                  disabled={busy}
                >
                  {c.NAME}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 選歌面板（同主題顯示多首） */}
        {currentCat && !currentPick && (
          <div className="songlist-panel">
            <div className="songlist-title">{currentCat.name}</div>
            <div className="song-tiles">
              {(currentCat.songs || []).map(s => {
                const used = (currentCat.pickedSongIds || []).includes(s.id);
                return (
                  <button
                    key={s.id}
                    className={`song-tile admin ${used ? 'used':''}`}
                    onClick={() => chooseSong(s.id)}
                    disabled={busy || used}
                    title={used ? '本輪已挑過' : '挑選這首'}
                  >
                    <div className="song-title">{s.title || '（未命名）'}</div>
                    <div className="song-meta">
                      {s.artist ? s.artist : '未知歌手'}
                      {s.year ? ` ・ ${s.year}` : ''}
                      <span className={`media-badge ${s.videoUrl ? 'has' : 'none'}`}>
                        {s.videoUrl ? '影片' : '無媒體'}
                      </span>
                    </div>
                    {used && <div className="used-mask">已挑過</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 輸入與確認（不顯示影片） */}
        {currentPick && (
          <div className="play-panel">
            <div className="play-admin-ctrl">
              <input
                className="ans-input"
                placeholder="輸入文字，前台即時顯示"
                value={input}
                onChange={(e) => onType(e.target.value)}
                disabled={busy}
              />
              <button className="btn-confirm" onClick={onConfirm} disabled={busy}>送出確認</button>
            </div>

            <div className="player empty">（後台不顯示影片，前台會自動播放）</div>
          </div>
        )}
      </div>
    </div>
  );
}
